<template>
  <div class="d-flex flex-column row-gap-3" data-locator="data">
    <div class="text--medium text--extra-bold">
      {{ $t('project.project-settings.ProjectSettingsInformation.title') }}
    </div>
    <AppCard class="pa-6 border" elevation="0" data-locator="data-table">
      <template v-if="roomStats === null">
        <v-row :class="{'my-4' : $vuetify.display.xs}">
          <v-col cols="12" md="3">
            <v-skeleton-loader type="text@3"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" md="3">
            <v-skeleton-loader type="text@3"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" md="3">
            <v-skeleton-loader type="text@3"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" md="3">
            <v-skeleton-loader type="text@3"></v-skeleton-loader>
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row :class="{'my-4' : $vuetify.display.xs}">
          <v-col cols="12" md="3" data-locator="additional-informations-column">
            <div>
              <font-awesome-icon :icon="['far', 'list-alt']"/>
              <span class="font-weight-bold pl-4">
                {{ $t('project.project-settings.ProjectSettingsInformation.additionalInformation') }}
              </span>
            </div>
            <v-container class="px-0">
              <v-row no-gutters class="mb-4">
                <v-col>
                  {{ $t('project.project-settings.ProjectSettingsInformation.createdAt') }}
                </v-col>
                <v-col class="pl-5">
                  <DateDisplay :value="currentRoom.createdAt"/>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col>
                  {{ $t('project.project-settings.ProjectSettingsInformation.duration') }}
                </v-col>
                <v-col class="pl-5">
                  {{ handleISOToRelativeTime(currentRoom.createdAt) }}
                </v-col>
              </v-row>
            </v-container>
          </v-col>
          <v-col cols="12" md="3"
                 :class="{dividerH : $vuetify.display.smAndDown, dividerV : $vuetify.display.mdAndUp}"
                 data-locator="documents-column"
          >
            <div>
              <font-awesome-icon :icon="['far', 'file-alt']"/>
              <span class="font-weight-bold pl-4">
                {{ $t('project.project-settings.ProjectSettingsInformation.documents') }}
              </span>
            </div>
            <v-container class="px-0">
              <v-row v-if="files" no-gutters class="mb-4">
                <v-col>
                  {{ $t('project.project-settings.ProjectSettingsInformation.documentsCountText') }}
                </v-col>
                <v-col class="pl-5">
                  <span class="font-weight-bold"
                        v-t="{
                          path: 'project.project-settings.ProjectSettingsInformation.documentsCount',
                          plural: files,
                          args: { count: files },
                        }"
                  />
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col>
                  {{ $t('project.project-settings.ProjectSettingsInformation.archivedDocumentsCountText') }}
                </v-col>
                <v-col class="pl-5">
                  <span class="font-weight-bold"
                        v-t="{
                          path: 'project.project-settings.ProjectSettingsInformation.documentsCount',
                          plural: archivedDocuments,
                          args: { count: archivedDocuments },
                        }"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-col>
          <v-col cols="12" md="3"
                 :class="{dividerH : $vuetify.display.smAndDown, dividerV : $vuetify.display.mdAndUp}"
                 data-locator="storage-column"
          >
            <div>
              <font-awesome-icon :icon="['far', 'sd-card']"/>
              <span class="font-weight-bold pl-4">
                {{ $t('project.project-settings.ProjectSettingsInformation.storage') }}
              </span>
            </div>

            <div class="my-3"
                 v-t="{
                   path: 'project.project-settings.ProjectSettingsInformation.storageData',
                   plural: Math.floor(usedStorage.storage),
                   args: {
                     totalCount: totalStorage.storage,
                     totalUnit: this.$t(`project.project-settings.ProjectSettingsInformation.${totalStorage.unit}`),
                     usedCount: usedStorage.storage,
                     usedUnit: this.$t(`project.project-settings.ProjectSettingsInformation.${usedStorage.unit}`),
                   },
                 }"
            />

            <div>
              <v-progress-linear :model-value="storagePercent"
                                 color="blue"
                                 rounded
                                 height="10"
              />
            </div>
          </v-col>
          <v-col cols="12" md="3"
                 :class="{dividerH : $vuetify.display.smAndDown, dividerV : $vuetify.display.mdAndUp}"
                 data-locator="signatures-column"
          >
            <div>
              <font-awesome-icon :icon="['far', 'file-signature']"/>
              <span class="font-weight-bold pl-4">{{ $t('project.project-settings.ProjectSettingsInformation.signatures') }}</span>
            </div>
            <template v-if="advancedSignature === null">
              <v-skeleton-loader type="text@3"></v-skeleton-loader>
            </template>
            <template v-else>
              <v-container class="px-0">
                <v-row no-gutters class="mb-4" data-locator="advanced-signature-data-row">
                    <v-col data-locator="signature-type">
                      {{
                        $t(
                          isBillingByEnvelope
                            ? 'project.project-settings.ProjectSettingsInformation.advancedEnvelopeSignatures'
                            : 'project.project-settings.ProjectSettingsInformation.advancedSignatures',
                        )
                      }}
                    </v-col>
                    <v-col class="pl-5">
                      <ProjectSettingsSignaturesCount :price="advancedSignature.cost"
                                                      :count="advancedSignature.count"
                                                      :is-billing-by-envelope="isBillingByEnvelope"
                                                      :documents-count="advancedSignature.documents"
                                                     
                      />
                    </v-col>
                </v-row>
                <v-row no-gutters class="mb-4" data-locator="simple-signature-data-row">
                  <v-col data-locator="signature-type">
                    {{
                      $t(isBillingByEnvelope
                        ? 'project.project-settings.ProjectSettingsInformation.simpleEnvelopeSignatures'
                        : 'project.project-settings.ProjectSettingsInformation.simpleSignatures')
                    }}
                  </v-col>
                  <v-col class="pl-5">
                    <ProjectSettingsSignaturesCount :price="simpleSignatures.cost"
                                                    :count="simpleSignatures.count"
                                                    :is-billing-by-envelope="isBillingByEnvelope"
                                                    :documents-count="simpleSignatures.documents"
                    />
                  </v-col>
                </v-row>
                <v-row no-gutters class="mb-4" data-locator="qualified-signature-data-row">
                  <v-col data-locator="signature-type">
                    {{ $t('project.project-settings.ProjectSettingsInformation.qualifiedSignatures') }}
                  </v-col>
                  <v-col class="pl-5">
                    <ProjectSettingsSignaturesCount
                      :price="qualifiedSignatures.cost"
                      :count="qualifiedSignatures.count"
                      :documents-count="qualifiedSignatures.documents"
                    />
                  </v-col>
                </v-row>
                <v-row no-gutters data-locator="handwritten-signature-data-row">
                  <v-col data-locator="signature-type">{{ $t('project.project-settings.ProjectSettingsInformation.handwrittenSignatures') }}</v-col>
                  <v-col class="pl-5" data-locator="signature-count"
                         v-t="{
                           path: 'project.project-settings.ProjectSettingsInformation.handwrittenSignaturesCount',
                           plural: handwrittenSignatures.count,
                           args: { number: handwrittenSignatures.count },
                         }"
                  >
                  </v-col>
                </v-row>
              </v-container>
            </template>
          </v-col>
        </v-row>
      </template>
    </AppCard>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import { ISOToRelativeTime } from '@/common/utils/dates'
import { toast } from '@/common/utils/toast'
import { GET_ROOM_STATS } from '@/store/modules/room/action_types'

import ProjectSettingsSignaturesCount from './signatures/ProjectSettingsSignaturesCount.vue'

export default {
  name: 'ProjectSettingsInformation',
  components: {
    AppCard,
    DateDisplay,
    ProjectSettingsSignaturesCount,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState('room', ['currentRoom', 'roomStats']),
    ...mapGetters('room', [
      'advancedSignature',
      'simpleSignatures',
      'handwrittenSignatures',
      'qualifiedSignatures',
      'archivedDocuments',
      'files',
      'storage',
      'totalStorage',
      'usedStorage',
      'storagePercent',
      'isBillingByEnvelope',
    ]),
  },
  mounted () {
    this.prepareGetRoomStats()
  },
  methods: {
    ...mapActions('room', [GET_ROOM_STATS]),
    async prepareGetRoomStats () {
      try {
        await this.GET_ROOM_STATS(this.mnemo)
      } catch (error) {
        toast.error(this.$t('project.project-settings.ProjectSettingsInformation.getRoomStatsError'))
      }
    },
    handleISOToRelativeTime (date) {
      return ISOToRelativeTime(date, true)
    },
  },
}
</script>

<style scoped lang="scss">
.dividerV {
  padding: 8px 30px;
  border-left: 1px solid #E0E0E0;

  &:last-child {
    padding-right: 12px;
  }
}

.dividerH {
  padding: 30px 8px;
  border-top: 1px solid #E0E0E0;

  &:last-child {
    padding-bottom: 0;
  }
}
</style>
